import React, {
  ReactElement,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Flex, Text } from 'rebass/styled-components';

import useGlobalSurveyColumns from './useGlobalSurveyColumns';

import { SlidePanel, SearchInput, Table } from 'ui/patterns';
import { Button, Stack } from 'ui/components';

import { useGetGlobalSurveys } from 'hooks';

import * as Icon from 'assets/icons';

import { SectionTypeInput } from 'types/forms';
import { SortItemType } from 'types/modalTypes';
import { GlobalSectionProps } from '../addEditFormTypes';

const sortItem = [{ sortKey: 'Title', sortOrder: 'asc' }];

export default function AddGlobalSection({
  showPanel,
  togglePanel,
  handleAddEditGlobalSection,
}: GlobalSectionProps): ReactElement {
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState<
    SortItemType | Array<SortItemType>
  >(sortItem);

  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search
  const [selectedGlobalSection, setGlobalSection] = useState<SectionTypeInput>(
    {},
  );
  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const columns = useGlobalSurveyColumns();

  const {
    data: globalSurveyList,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useGetGlobalSurveys({
    keywordSearch,
    columnsSearch,
    sortItem: currentSortItem,
  });

  const tableData = useMemo(() => {
    if (globalSurveyList) {
      return globalSurveyList?.pages
        ?.map((dataItem: any) => dataItem.Data)
        .flat();
    }
  }, [globalSurveyList]);

  const totalItems = useMemo(() => {
    if (globalSurveyList) {
      return globalSurveyList.pages[0].FilteredRecords;
    }
    return 0;
  }, [globalSurveyList]);

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (currentSortItem instanceof Array) {
        const existingItem = currentSortItem.find(
          sItem => sItem.sortKey === item,
        );
        if (existingItem) {
          setCurrentSortItem({
            sortKey: item,
            sortOrder: existingItem.sortOrder === 'asc' ? 'desc' : 'asc',
          });
        } else {
          setCurrentSortItem({
            sortKey: item,
            sortOrder,
          });
        }
      } else if (currentSortItem.sortKey && currentSortItem.sortOrder) {
        if (
          currentSortItem.sortKey === item &&
          currentSortItem.sortOrder === 'asc'
        ) {
          sortOrder = 'desc';
        }
        setCurrentSortItem({
          sortKey: item,
          sortOrder,
        });
      }
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setGlobalSection({});
  };

  const handleRowClick = (row: any): void => {
    setGlobalSection(row.original);
  };

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const addGlobalSectionActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          setGlobalSection({});
        }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        variant="primary"
        onClick={() => {
          handleAddEditGlobalSection(selectedGlobalSection);
          togglePanel();
          setGlobalSection({});
        }}
        disabled={(selectedGlobalSection?.Id ? false : true) || !showPanel}
      >
        Save
      </Button>
    </>
  );
  return (
    <>
      <SlidePanel
        title="Add Global Section"
        size="lg"
        actions={addGlobalSectionActions()}
        onClose={() => {
          togglePanel();
          setGlobalSection({});
        }}
        open={showPanel}
      >
        <Flex flexDirection="column" height="100%">
          <Flex justifyContent="space-between" alignItems="center" mb={2}>
            <Text fontSize={1} color="text.body" sx={{ height: '20px' }}>
              {tableData &&
                tableData.length > 0 &&
                !isFetching &&
                `Showing ${tableData.length} of ${totalItems}${
                  isFetching ? ' | Processing' : ''
                }`}
            </Text>
            <Stack direction="x" gap={2}>
              <SearchInput
                placeholder="Search"
                handleChange={handleKeywordSearch}
                value={keywordSearch}
              />
              <Button
                iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
                variant="secondary"
                onClick={handleReset}
              >
                Reset All
              </Button>
            </Stack>
          </Flex>
          <Stack direction="y" gap={4} style={{ flex: 1 }}>
            <Table
              height="auto"
              columns={columns}
              data={tableData ? tableData : []}
              isLoading={isLoading || isFetching}
              headerUpdateCallBack={headerUpdateCallBack}
              handleSortChange={handleSortChange}
              multiSortedItem={currentSortItem}
              columnsSearch={columnsSearch}
              noOptions
              onRowClick={handleRowClick}
              selectedRowId={selectedGlobalSection?.Id}
              onEndReached={onEndReached}
              isFetching={!!isFetchingNextPage}
            />
          </Stack>
        </Flex>
      </SlidePanel>
    </>
  );
}
